import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ADMIN, ROLE_LABELS, roleRequestPath } from '@/utils/roles';
import '@/styles/roles.css';

function Loader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--cream)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid var(--navy)', borderTopColor: 'var(--glt)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontFamily: 'var(--f-branding)', fontSize: 18, fontWeight: 700 }}>
          Colis<em style={{ color: 'var(--gold)', fontStyle: 'normal' }}>2</em>Ship
        </div>
      </div>
    </div>
  );
}

/**
 * Page réservée à un rôle que le compte n'a pas : on dit lequel et on mène à sa demande, déjà choisie, avec cette page
 * en retour (la notification de validation y ramène). Les pages de l'équipe (admin) ne se demandent pas.
 */
function NotAuthorized({ requiredRoles = [] }) {
  const location = useLocation();
  const memberRole = requiredRoles.find((r) => r !== ADMIN);
  const label = ROLE_LABELS[memberRole];
  return (
    <div className="na-page" data-testid="not-authorized">
      <div className="na-card">
        <div className="na-ico"><Lock size={26} aria-hidden="true" /></div>
        <h1 className="na-title">{memberRole ? `Page réservée au rôle ${label}` : 'Page réservée à l’équipe Colis2Ship'}</h1>
        <p className="na-text">
          {memberRole
            ? `Pour ouvrir cette page, il vous faut le rôle ${label}. Faites-en la demande : dès que notre équipe la valide, une notification vous ramène ici.`
            : 'Cette page est réservée aux administrateurs de Colis2Ship.'}
        </p>
        <div className="na-actions">
          {memberRole && (
            <Link className="na-btn" to={roleRequestPath(memberRole, location.pathname)} data-testid="not-authorized-request-role">
              Demander le rôle {label}
            </Link>
          )}
          <Link className="na-link" to="/app/dashboard" data-testid="back-to-dashboard">Retour au tableau de bord</Link>
        </div>
      </div>
    </div>
  );
}

export function ProtectedRoute({ children, requiredRoles = null }) {
  const { user, checkAuth } = useAuth();
  const location = useLocation();
  const missingRole = !!user && !!requiredRoles?.length && !requiredRoles.some((r) => (user.roles || []).includes(r));
  // Le compte est chargé une fois : un rôle accordé depuis (notification de validation) n'y est pas encore. On le relit
  // une fois avant de refuser.
  const [rechecked, setRechecked] = useState(false);
  useEffect(() => {
    if (!missingRole || rechecked) return undefined;
    let alive = true;
    checkAuth().finally(() => { if (alive) setRechecked(true); });
    return () => { alive = false; };
  }, [missingRole, rechecked, checkAuth]);

  if (user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (missingRole) return rechecked ? <NotAuthorized requiredRoles={requiredRoles} /> : <Loader />;
  return children;
}

export function AdminRoute({ children }) {
  const { user } = useAuth();

  if (user === undefined) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--navy)', borderTopColor: 'var(--glt)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!user.roles?.includes('ROLE_ADMIN')) return <NotAuthorized requiredRoles={['ROLE_ADMIN']} />;

  return children;
}
